import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { CompactKpiCard } from '@/components/kpi-card';
import type { KpiAccentVariant } from '@/components/kpi-card';
import { Badge } from '@/components/ui/badge';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DataTableEnhanced } from '@/components/ui/data-table-enhanced';
import type { ColumnDef, ColumnSort } from '@/components/ui/data-table-enhanced';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { SensitiveData } from '@/components/sensitive-data';
import PageHeaderBlock from '@/registry/evo-shadcn/blocks/page-header';
import {
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  Info,
  TrendingUp,
  TrendingDown,
  Minus,
  BarChart3,
  Search,
  X,
  LayoutGrid,
  CircleCheck,
  TriangleAlert,
  CircleX,
  Download,
  Loader2,
} from 'lucide-react';
import { fetchProjects, fetchProjectById, fetchBudgetVarianceData, getDataMode } from '@/lib/data-service';
import { toast } from 'sonner';
import { fetchAllProjectPages } from '@/lib/export-projects-excel';
import { exportBudgetVarianceToExcel } from '@/lib/export-budget-variance-excel';
import type { ProjectFilterParams } from '@/lib/data-service';
import { getBudgetHealthThresholds } from '@/lib/budget-health-thresholds-storage';
import { formatCurrency, healthToVariant } from '@/lib/utils';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { paginateArray, DEFAULT_PAGE_SIZE } from '@/types/pagination';
import type { PaginationMetadata, PaginationParams } from '@/types/pagination';
import { PaginationControls } from '@/components/pagination-controls';
import type { Project } from '@/types';
import type {
  CostCategory,
  BudgetStatus,
  BudgetVarianceReport,
  BudgetVarianceCategory,
} from '@/types/budget.types';
import { useCompany } from '@/contexts/CompanyContext';
import { useErrorHandler } from '@/hooks/use-error-handler';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function deriveCategoryStatus(progressPercent: number): BudgetStatus {
  const thresholds = getBudgetHealthThresholds();
  if (progressPercent >= thresholds.costRedThreshold) return 'red';
  if (progressPercent >= thresholds.costAmberThreshold) return 'amber';
  return 'green';
}

function VarianceValue({
  value,
  isPercent = false,
  invertColour = false,
}: {
  value: number;
  isPercent?: boolean;
  invertColour?: boolean;
}) {
  // Over budget (positive) = bad = red. Under budget (negative) = good = green.
  // invertColour flips this (e.g. for revenue, positive = good)
  const isOverBudget = value > 0;
  const isOnBudget = Math.abs(isPercent ? value : value) < (isPercent ? 2 : 0.01);

  let colourClass = 'text-muted-foreground';
  if (!isOnBudget) {
    const isBad = invertColour ? !isOverBudget : isOverBudget;
    colourClass = isBad ? 'text-destructive' : 'text-success-600 dark:text-success-500';
  }

  const prefix = value > 0 ? '+' : '';
  const formatted = isPercent
    ? `${prefix}${value.toFixed(1)}%`
    : `${prefix}${formatCurrency(value)}`;

  return <span className={colourClass}>{formatted}</span>;
}

function HealthBadge({ status }: { status: BudgetStatus }) {
  const variant = healthToVariant(status);
  const label = status === 'green' ? 'On Track' : status === 'amber' ? 'At Risk' : 'Over Budget';
  return (
    <Badge variant={variant} className="text-xs gap-1.5">
      <StatusIndicator variant={variant === 'destructive' ? 'error' : variant} size="sm" />
      {label}
    </Badge>
  );
}

function budgetStatusToAccent(status: BudgetStatus): 'success' | 'warning' | 'destructive' {
  if (status === 'red') return 'destructive';
  if (status === 'amber') return 'warning';
  return 'success';
}

function budgetStatusToKpiAccent(status: BudgetStatus): KpiAccentVariant {
  if (status === 'red') return 'error';
  if (status === 'amber') return 'warning';
  return 'success';
}

// ─────────────────────────────────────────────────────────────────────────────
// Category Row
// ─────────────────────────────────────────────────────────────────────────────
// Mirrors the Cost Budget table: only Labour is expandable (to show roles);
// all other categories are flat rows with no chevron.

function CategoryRow({
  category,
  budgetOverride,
  actualOverride,
  showCommitted,
  isExpanded,
  onToggle,
  isLabourGroup = false,
}: {
  category: CostCategory | BudgetVarianceCategory;
  budgetOverride?: number;
  actualOverride?: number;
  showCommitted: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  isLabourGroup?: boolean;
}) {
  const budget = budgetOverride !== undefined ? budgetOverride : category.budget;
  const actual = actualOverride !== undefined ? actualOverride : category.actual;
  const committed = isLabourGroup ? null : category.committed;
  const available = budget - actual - (committed ?? 0);
  const variance = actual - budget;
  const variancePct = budget !== 0 ? (variance / budget) * 100 : 0;
  const status = deriveCategoryStatus(budget > 0 ? (actual / budget) * 100 : 0);

  const isOverCommitted = available < 0;

  return (
    <TableRow className={isLabourGroup ? 'bg-muted/40' : ''}>
      {/* Name */}
      <TableCell className="font-medium">
        <div className="flex items-center gap-1.5">
          {isLabourGroup ? (
            <button
              onClick={onToggle}
              className="p-0.5 rounded hover:bg-accent transition-colors"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? (
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              )}
            </button>
          ) : (
            <span className="w-5 inline-block" />
          )}
          <span>{category.name}</span>
        </div>
      </TableCell>

      {/* Budget */}
      <TableCell className="text-right tabular-nums">
        <SensitiveData>{formatCurrency(budget)}</SensitiveData>
      </TableCell>

      {/* Actual */}
      <TableCell className="text-right tabular-nums">
        <SensitiveData>{formatCurrency(actual)}</SensitiveData>
      </TableCell>

      {/* Committed */}
      {showCommitted && (
        <TableCell className="text-right tabular-nums">
          {committed === null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <SensitiveData>{formatCurrency(committed)}</SensitiveData>
          )}
        </TableCell>
      )}

      {/* Available */}
      <TableCell className="text-right tabular-nums">
        <div className="flex items-center justify-end gap-1">
          <SensitiveData>
            <span className={isOverCommitted ? 'text-destructive' : ''}>
              {formatCurrency(available)}
            </span>
          </SensitiveData>
          {isOverCommitted && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Committed costs exceed remaining budget</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </TableCell>

      {/* Variance £ */}
      <TableCell className="text-right tabular-nums">
        <SensitiveData>
          <VarianceValue value={variance} />
        </SensitiveData>
      </TableCell>

      {/* Variance % */}
      <TableCell className="text-right tabular-nums">
        <VarianceValue value={variancePct} isPercent />
      </TableCell>

      {/* Status */}
      <TableCell>
        <HealthBadge status={status} />
      </TableCell>
    </TableRow>
  );
}

// Labour sub-row — renders individual labour categories (Consulting, Development, Design)
// indented beneath the rolled-up Labour group row. Matches the Cost Budget table drill-down style.
function LabourSubRow({
  cat,
  showCommitted,
}: {
  cat: CostCategory | BudgetVarianceCategory;
  showCommitted: boolean;
}) {
  const budget = cat.budget;
  const actual = cat.actual;
  const committed = null; // labour committed shown as — (same as Cost Budget table)
  const available = budget - actual;
  const variance = actual - budget;
  const variancePct = budget !== 0 ? (variance / budget) * 100 : 0;
  const status = deriveCategoryStatus(budget > 0 ? (actual / budget) * 100 : 0);

  return (
    <TableRow className="bg-muted/20">
      <TableCell className="text-sm" style={{ paddingLeft: '48px' }}>
        <div className="flex items-center gap-1.5">
          <span className="w-5 inline-block" />
          {cat.name}
        </div>
      </TableCell>
      <TableCell className="text-right tabular-nums text-sm">
        <SensitiveData>{formatCurrency(budget)}</SensitiveData>
      </TableCell>
      <TableCell className="text-right tabular-nums text-sm">
        <SensitiveData>{formatCurrency(actual)}</SensitiveData>
      </TableCell>
      {showCommitted && (
        <TableCell className="text-right tabular-nums text-sm text-muted-foreground">—</TableCell>
      )}
      <TableCell className="text-right tabular-nums text-sm">
        <SensitiveData>
          <span className={available < 0 ? 'text-destructive' : ''}>
            {formatCurrency(available)}
          </span>
        </SensitiveData>
      </TableCell>
      <TableCell className="text-right tabular-nums text-sm">
        <SensitiveData>
          <VarianceValue value={variance} />
        </SensitiveData>
      </TableCell>
      <TableCell className="text-right tabular-nums text-sm">
        <VarianceValue value={variancePct} isPercent />
      </TableCell>
      <TableCell>
        <HealthBadge status={status} />
      </TableCell>
    </TableRow>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Portfolio Mode 1 View
// ─────────────────────────────────────────────────────────────────────────────

type EnrichedVarianceProject = Project & {
  revenueVariance: number;
  revenuePct: number;
  overallStatus: BudgetStatus;
};

const STORAGE_KEY_PREFIX = 'budget-variance-report:';

function PortfolioMode1View({
  projects,
  onSelectProject,
  onRefetch,
  pagination: serverPagination,
}: {
  projects: Project[];
  onSelectProject: (id: number) => void;
  onRefetch?: (filters: ProjectFilterParams, paginationParams?: PaginationParams) => void;
  pagination?: PaginationMetadata | null;
}) {
  const isLive = getDataMode() === 'live';
  const thresholds = useMemo(() => getBudgetHealthThresholds(), []);
  // Filters, sorting and the current page are persisted for the session so
  // drilling into a project and navigating back restores the same view.
  const [searchTerm, setSearchTerm]     = usePersistedState(`${STORAGE_KEY_PREFIX}search`, '');
  const [billableFilter, setBillableFilter] = usePersistedState<string>(`${STORAGE_KEY_PREFIX}billable`, 'all');
  const [columnSorts, setColumnSorts]   = usePersistedState<ColumnSort[]>(`${STORAGE_KEY_PREFIX}sorts`, [{ columnId: 'status', direction: 'desc' }]);
  const [currentPage, setPage] = usePersistedState(`${STORAGE_KEY_PREFIX}page`, 1);
  const [pageSize, setPageSize] = usePersistedState(`${STORAGE_KEY_PREFIX}page-size`, DEFAULT_PAGE_SIZE);
  const [isFetching, setIsFetching]     = useState(false);
  const [isExporting, setIsExporting]   = useState(false);

  // Build current filter params for server-side requests
  const buildFilters = useCallback((): ProjectFilterParams => {
    const filters: ProjectFilterParams = {};
    if (searchTerm.trim()) filters.search = searchTerm.trim();
    if (billableFilter !== 'all') filters.billable = billableFilter;
    return filters;
  }, [searchTerm, billableFilter]);

  const filtersRef = useRef<ProjectFilterParams>({});
  filtersRef.current = buildFilters();

  // Server-side refetch when search changes (live mode only). The first run
  // restores the persisted page instead of resetting to page 1.
  const isRestoringRef = useRef(true);
  useEffect(() => {
    if (!isLive || !onRefetch) return;
    const restoring = isRestoringRef.current;
    isRestoringRef.current = false;
    if (!restoring && currentPage !== 1) setPage(1);
    const page = restoring ? currentPage : 1;
    const handler = setTimeout(() => {
      setIsFetching(true);
      Promise.resolve(onRefetch(filtersRef.current, { page, pageSize }))
        .finally(() => setIsFetching(false));
    }, restoring ? 0 : 300);
    return () => clearTimeout(handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLive, onRefetch, searchTerm, billableFilter]);

  // Server-side pagination handlers (live mode only)
  const handlePageChange = useCallback((page: number) => {
    if (!onRefetch || !serverPagination) return;
    setPage(page);
    setIsFetching(true);
    Promise.resolve(onRefetch(filtersRef.current, { page, pageSize: serverPagination.pageSize }))
      .finally(() => setIsFetching(false));
  }, [onRefetch, serverPagination, setPage]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    if (!onRefetch) return;
    setPage(1);
    setPageSize(newPageSize);
    setIsFetching(true);
    Promise.resolve(onRefetch(filtersRef.current, { page: 1, pageSize: newPageSize }))
      .finally(() => setIsFetching(false));
  }, [onRefetch, setPage, setPageSize]);

  const enriched = useMemo<EnrichedVarianceProject[]>(() =>
    projects.map(p => {
      const revenueVariance = (p.actualSales ?? 0) - (p.budgetRevenue ?? 0);
      const revenuePct      = (p.budgetRevenue ?? 0) !== 0 ? (revenueVariance / (p.budgetRevenue ?? 1)) * 100 : 0;
      const costPct         = Math.abs(p.costVariancePercent ?? 0);
      const hoursPct        = Math.abs(p.hoursVariancePercent ?? 0);
      const worstPct        = Math.max(costPct, Math.abs(revenuePct), hoursPct);
      const overallStatus: BudgetStatus =
        worstPct >= thresholds.costRedThreshold   ? 'red'
        : worstPct >= thresholds.costAmberThreshold ? 'amber'
        : 'green';
      return { ...p, revenueVariance, revenuePct, overallStatus };
    }),
    [projects, thresholds],
  );

  // Summary counts — in live mode use pagination totalItems for total
  const summary = useMemo(() => ({
    total: isLive && serverPagination ? serverPagination.totalItems : enriched.length,
    green: enriched.filter(p => p.overallStatus === 'green').length,
    amber: enriched.filter(p => p.overallStatus === 'amber').length,
    red:   enriched.filter(p => p.overallStatus === 'red').length,
  }), [enriched, isLive, serverPagination]);

  // Reset to page 1 when search changes (demo/tour only)
  useEffect(() => {
    if (!isLive) setPage(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, isLive]);

  // Client-side search filter (demo/tour only)
  const filtered = useMemo(() => {
    if (isLive) return enriched;
    let result = enriched;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(q) ||
        (p.code ?? '').toLowerCase().includes(q),
      );
    }
    if (billableFilter === 'billable') result = result.filter(p => p.billable !== false);
    if (billableFilter === 'non-billable') result = result.filter(p => p.billable === false);
    return result;
  }, [enriched, searchTerm, billableFilter, isLive]);

  // Sort (client-side for both modes — applied to current page data)
  const sorted = useMemo(() => {
    if (columnSorts.length === 0) return filtered;
    const arr = [...filtered];
    const STATUS_ORDER: Record<BudgetStatus, number> = { red: 0, amber: 1, green: 2 };
    arr.sort((a, b) => {
      for (const { columnId, direction } of columnSorts) {
        let cmp = 0;
        switch (columnId) {
          case 'status':          cmp = STATUS_ORDER[a.overallStatus] - STATUS_ORDER[b.overallStatus]; break;
          case 'name':            cmp = a.name.localeCompare(b.name); break;
          case 'budgetRevenue':   cmp = (a.budgetRevenue ?? 0) - (b.budgetRevenue ?? 0); break;
          case 'actualRevenue':   cmp = (a.actualSales ?? 0) - (b.actualSales ?? 0); break;
          case 'revenueVariance': cmp = a.revenueVariance - b.revenueVariance; break;
          case 'costVariance':    cmp = (a.costVariance ?? 0) - (b.costVariance ?? 0); break;
          case 'hoursVariance':   cmp = (a.hoursVariance ?? 0) - (b.hoursVariance ?? 0); break;
        }
        if (cmp !== 0) return direction === 'asc' ? cmp : -cmp;
      }
      return a.name.localeCompare(b.name);
    });
    return arr;
  }, [filtered, columnSorts]);

  // ── Excel export ───────────────────────────────────────────────────
  const handleExportExcel = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    toast.info('Export started — your download will begin shortly.');

    try {
      if (!isLive) {
        await exportBudgetVarianceToExcel(sorted);
        toast.success('Export complete — file downloaded.');
        return;
      }

      const allProjects = await fetchAllProjectPages('budget-variance-report', filtersRef.current);
      await exportBudgetVarianceToExcel(allProjects);
      toast.success(`Export complete — ${allProjects.length} projects downloaded.`);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, isLive, sorted]);

  // Paginate — client-side for demo/tour, server data used as-is for live
  const paginated = useMemo(
    () => isLive && serverPagination
      ? { data: sorted, pagination: serverPagination }
      : paginateArray(sorted, currentPage, pageSize),
    [sorted, currentPage, pageSize, isLive, serverPagination],
  );

  const columns = useMemo<ColumnDef<EnrichedVarianceProject, unknown>[]>(() => [
    {
      id: 'status',
      header: 'Budget Status',
      cell: ({ row }) => <HealthBadge status={row.original.overallStatus} />,
      meta: { width: '130px' },
    },
    {
      id: 'name',
      header: 'Project',
      cell: ({ row }) => (
        <div>
          <div className="font-medium text-sm">{row.original.name}</div>
          {row.original.code && (
            <div className="text-xs text-muted-foreground">{row.original.code}</div>
          )}
        </div>
      ),
    },
    {
      id: 'budgetRevenue',
      header: () => <span className="block text-right">Budget Revenue</span>,
      cell: ({ row }) => (
        <div className="text-right text-sm">
          <SensitiveData>{formatCurrency(row.original.budgetRevenue ?? 0)}</SensitiveData>
        </div>
      ),
      meta: { align: 'right' },
    },
    {
      id: 'actualRevenue',
      header: () => <span className="block text-right">Actual Revenue</span>,
      cell: ({ row }) => (
        <div className="text-right text-sm">
          <SensitiveData>{formatCurrency(row.original.actualSales ?? 0)}</SensitiveData>
        </div>
      ),
      meta: { align: 'right' },
    },
    {
      id: 'revenueVariance',
      header: () => <span className="block text-right">Revenue Variance</span>,
      cell: ({ row }) => (
        <div className="text-right text-sm">
          <SensitiveData>
            <VarianceValue value={row.original.revenueVariance} invertColour />
          </SensitiveData>
        </div>
      ),
      meta: { align: 'right' },
    },
    {
      id: 'costVariance',
      header: () => <span className="block text-right">Cost Variance</span>,
      cell: ({ row }) => (
        <div className="text-right text-sm">
          <SensitiveData>
            <VarianceValue value={row.original.costVariance ?? 0} />
          </SensitiveData>
        </div>
      ),
      meta: { align: 'right' },
    },
    {
      id: 'hoursVariance',
      header: () => <span className="block text-right">Hours Variance</span>,
      cell: ({ row }) => (
        <div className="text-right text-sm">
          <SensitiveData>
            <span className={
              (row.original.hoursVariance ?? 0) > 0 ? 'text-error-600 dark:text-error-400' :
              (row.original.hoursVariance ?? 0) < 0 ? 'text-success-600 dark:text-success-400' :
              'text-muted-foreground'
            }>
              {(row.original.hoursVariance ?? 0) > 0 ? '+' : ''}
              {row.original.hoursVariance ?? 0} hrs
            </span>
          </SensitiveData>
        </div>
      ),
      meta: { align: 'right' },
    },
    {
      id: 'billable',
      header: 'Billable',
      cell: ({ row }) => {
        const isBillable = row.original.billable ?? true;
        return isBillable
          ? <Badge variant="outline" className="text-xs text-emerald-700 bg-emerald-50 border-emerald-200">Billable</Badge>
          : <Badge variant="outline" className="text-xs text-muted-foreground">Non-billable</Badge>;
      },
    },
  ], []);

  return (
    <div className="flex flex-col gap-6">
      {/* RAG summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <CompactKpiCard
          title="Total Projects"
          value={summary.total}
          description="All active projects"
          icon={LayoutGrid}
          accentVariant="info"
          className="shadow-none"
        />
        <CompactKpiCard
          title="On Budget"
          value={summary.green}
          description="Green status"
          icon={CircleCheck}
          accentVariant="success"
          className="shadow-none"
        />
        <CompactKpiCard
          title="At Risk"
          value={summary.amber}
          description="Amber status"
          icon={TriangleAlert}
          accentVariant="warning"
          className="shadow-none"
        />
        <CompactKpiCard
          title="Over Budget"
          value={summary.red}
          description="Red status"
          icon={CircleX}
          accentVariant="error"
          className="shadow-none"
        />
      </div>

      {/* Search + table */}
      <Card data-tour="budget-variance-filters">
        <CardContent className="p-0">
          {/* Search + Filters + Export */}
          <div className="flex gap-2 p-6 pb-4" data-tour="budget-variance-table">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by project name or code..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchTerm && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>

            {/* Billable filter */}
            <Select value={billableFilter} onValueChange={setBillableFilter}>
              <SelectTrigger className="h-9 w-36 text-sm">
                <SelectValue placeholder="All projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All projects</SelectItem>
                <SelectItem value="billable">Billable</SelectItem>
                <SelectItem value="non-billable">Non-billable</SelectItem>
              </SelectContent>
            </Select>

            {/* Export to Excel */}
            <Button
              variant="outline"
              onClick={handleExportExcel}
              disabled={isExporting}
              className="h-9 w-9 p-0"
              aria-label="Export to Excel"
              title="Export to Excel"
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
            </Button>
          </div>

          <DataTableEnhanced
            columns={columns}
            data={paginated.data}
            columnSorts={columnSorts}
            onColumnSortChange={setColumnSorts}
            onRowClick={row => onSelectProject(row.id)}
            getRowId={row => String(row.id)}
            loading={isFetching}
            className="rounded-none border-0 border-t"
            emptyMessage={
              searchTerm
                ? <span>No projects match <strong>{searchTerm}</strong>.</span>
                : 'No projects found.'
            }
            paginationContent={
              isLive && serverPagination && serverPagination.totalItems > 0 ? (
                <PaginationControls
                  pagination={serverPagination}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  showPageSizeSelector
                />
              ) : !isLive && paginated.pagination.totalPages > 1 ? (
                <PaginationControls
                  pagination={paginated.pagination}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                  showPageSizeSelector
                />
              ) : null
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

export function BudgetVarianceReportPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentCompany } = useCompany();
  const { handleApiError } = useErrorHandler({ useToast: true });

  // Read ?managerId= from URL (passed by homepage PM quick link)
  const initManagerId = searchParams.get('managerId') ? Number(searchParams.get('managerId')) : null;

  // Read ?reportProjectId= for deep-linking from the project reports tab
  const initReportProjectId = searchParams.get('reportProjectId');

  const [projects, setProjects] = useState<Project[]>([]);
  const [pagination, setPagination] = useState<PaginationMetadata | null>(null);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    initReportProjectId ? Number(initReportProjectId) : null
  );

  // Deep-linked project fetched by ID (may not be in the paginated list)
  const [deepLinkedProject, setDeepLinkedProject] = useState<Project | null>(null);

  useEffect(() => {
    if (initReportProjectId) {
      fetchProjectById(Number(initReportProjectId)).then((p) => {
        if (p) setDeepLinkedProject(p as Project);
      }).catch(() => { /* ignore */ });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Drill-down state is mirrored in the URL so browser back/forward moves
  // between the project detail and the portfolio grid.
  useEffect(() => {
    const idParam = searchParams.get('reportProjectId');
    setSelectedProjectId(idParam ? Number(idParam) : null);
  }, [searchParams]);

  const handleSelectProject = useCallback((id: number) => {
    setSelectedProjectId(id);
    const params = new URLSearchParams(searchParams);
    params.set('reportProjectId', String(id));
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const handleBack = useCallback(() => {
    setSelectedProjectId(null);
    const params = new URLSearchParams(searchParams);
    params.delete('reportProjectId');
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const [selectedRevisionId, setSelectedRevisionId] = useState<number | 'active'>('active');
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [reportData, setReportData] = useState<BudgetVarianceReport | null>(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [showCommitted, setShowCommitted] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('budget-variance-show-committed');
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });

  // Helper to extract projects + pagination from API response
  const applyProjectResult = useCallback((projectResult: any) => {
    if (projectResult && typeof projectResult === 'object' && 'data' in projectResult) {
      setProjects(projectResult.data as Project[]);
      if ('pagination' in projectResult) setPagination(projectResult.pagination as PaginationMetadata);
    } else {
      setProjects(Array.isArray(projectResult) ? projectResult : []);
      setPagination(null);
    }
  }, []);

  // Load projects (server-side paginated in live mode)
  useEffect(() => {
    setLoadingProjects(true);
    fetchProjects('projects', { page: 1, pageSize: DEFAULT_PAGE_SIZE })
      .then((result) => applyProjectResult(result))
      .catch((err) => handleApiError(err, 'Loading projects'))
      .finally(() => setLoadingProjects(false));
  }, [currentCompany?.id]);

  // Re-fetch projects with server-side filters + pagination (called by PortfolioMode1View)
  const handleRefetch = useCallback(async (filters: ProjectFilterParams, paginationParams?: PaginationParams) => {
    try {
      const effectivePagination = paginationParams ?? { page: 1, pageSize: pagination?.pageSize ?? DEFAULT_PAGE_SIZE };
      const projectResult = await fetchProjects('projects', effectivePagination, filters);
      applyProjectResult(projectResult);
    } catch (err) {
      handleApiError(err, 'Failed to filter projects');
    }
  }, [applyProjectResult, handleApiError, pagination?.pageSize]);

  // Reset revision when project changes
  useEffect(() => {
    setSelectedRevisionId('active');
    setExpandedCategories(new Set());
    setReportData(null);
  }, [selectedProjectId]);

  // Fetch report data when project or revision changes
  useEffect(() => {
    if (selectedProjectId === null) {
      setReportData(null);
      return;
    }
    setLoadingReport(true);
    const revId = selectedRevisionId === 'active' ? undefined : selectedRevisionId;
    fetchBudgetVarianceData(selectedProjectId, revId)
      .then((data) => setReportData(data))
      .catch((err) => handleApiError(err, 'Loading budget variance data'))
      .finally(() => setLoadingReport(false));
  }, [selectedProjectId, selectedRevisionId, currentCompany?.id]);

  // Toggle committed column persistence
  const toggleCommitted = () => {
    setShowCommitted((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('budget-variance-show-committed', String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === selectedProjectId) ?? deepLinkedProject ?? null,
    [projects, selectedProjectId, deepLinkedProject]
  );

  // Derive values from report data
  const revisions = reportData?.revisions ?? [];
  const costTotals = reportData?.costs.totals;
  const costCategories = reportData?.costs.categories ?? [];
  const isHistoricalRevision = selectedRevisionId !== 'active' && reportData?.baseline?.isActiveRevision === false;

  // Summary card calculations
  const totalBudget = costTotals?.budget ?? 0;
  const costVariance = costTotals?.variance ?? 0;
  const costVariancePct = costTotals?.variancePercent ?? 0;
  const costStatus = deriveCategoryStatus(
    totalBudget !== 0 ? ((costTotals?.actual ?? 0) / totalBudget) * 100 : 0
  );

  const revenueVariance = reportData?.revenue.variance ?? 0;
  const revenuePct = reportData?.revenue.variancePercent ?? 0;
  const revenueStatus = deriveCategoryStatus(
    Math.abs(revenuePct) < 2 ? 0 : revenuePct < 0 ? 85 : 110
  );

  const hoursVariance = reportData?.hours.variance ?? 0;
  const hoursPct = reportData?.hours.variancePercent ?? 0;
  const hoursStatus = deriveCategoryStatus(
    totalBudget !== 0 && reportData?.hours.budget
      ? ((reportData.hours.actual) / (reportData.hours.budget || 1)) * 100
      : 0
  );

  const availableBudget = costTotals?.available ?? 0;
  const availableStatus: BudgetStatus =
    availableBudget < 0 ? 'red' : availableBudget < totalBudget * 0.1 ? 'amber' : 'green';

  const toggleCategory = (id: number) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const hasCostBudget = costCategories.length > 0;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 pb-10">
      <PageHeaderBlock
        title="Budget Variance Report"
        subtitle={
          selectedProject
            ? `${selectedProject.code ? `${selectedProject.code} — ` : ''}${selectedProject.name}`
            : 'Planned vs actual comparison across revenue, cost, and hours.'
        }
      />

      {/* Control Bar — only shown in mode 2 (project selected) */}
      {selectedProjectId !== null && (
        <div className="flex flex-row gap-3 items-center">
          {/* Back button */}
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 -ml-2 text-muted-foreground hover:text-foreground"
            onClick={handleBack}
          >
            <ChevronRight className="h-4 w-4 rotate-180" />
            All Projects
          </Button>

          <div className="flex-1" />

          {/* Revision Selector */}
          {revisions.length > 0 && (
            <div className="shrink-0">
              <Select
                value={String(selectedRevisionId)}
                onValueChange={(val) => {
                  setSelectedRevisionId(val === 'active' ? 'active' : parseInt(val, 10));
                }}
              >
                <SelectTrigger className="w-[240px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active Revision</SelectItem>
                  {revisions.length > 0 && (
                    <div className="border-t my-1" />
                  )}
                  {revisions.map((r) => (
                    <SelectItem key={r.id} value={String(r.id)}>
                      {r.label}
                      {r.isActive ? ' [active]' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}

      {/* Historical Revision Banner */}
      {isHistoricalRevision && reportData?.baseline && (
        <div className="flex items-start gap-3 rounded-lg border border-warning-200 bg-warning-50 dark:bg-warning-950/20 dark:border-warning-800 px-4 py-3 text-sm">
          <AlertTriangle className="h-4 w-4 text-warning-600 dark:text-warning-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <span className="text-warning-800 dark:text-warning-300">
              Showing variance against {reportData.baseline.revisionLabel}.
              This is not the current approved budget.
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-warning-700 dark:text-warning-400 hover:text-warning-900 shrink-0 h-auto py-0"
            onClick={() => setSelectedRevisionId('active')}
          >
            Switch to Active Revision
          </Button>
        </div>
      )}

      {/* Portfolio overview — no project selected */}
      {selectedProjectId === null && (
        loadingProjects ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="h-full shadow-md">
                <CardContent className="px-4 pb-4 pt-4">
                  <Skeleton className="h-4 w-24 mb-3" />
                  <Skeleton className="h-8 w-16 mb-2" />
                  <Skeleton className="h-3 w-28" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <PortfolioMode1View
            projects={initManagerId ? projects.filter(p => p.projectManagerId === initManagerId) : projects}
            onSelectProject={handleSelectProject}
            onRefetch={handleRefetch}
            pagination={pagination}
          />
        )
      )}

      {/* Loading report data */}
      {selectedProjectId !== null && loadingReport && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="h-full shadow-md">
              <CardContent className="px-4 pb-4 pt-4">
                <Skeleton className="h-4 w-24 mb-3" />
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-3 w-28" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* No cost budget */}
      {selectedProjectId !== null && !loadingReport && !hasCostBudget && (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Info className="h-12 w-12 text-muted-foreground mb-4 opacity-40" />
          <p className="text-lg font-medium text-muted-foreground">
            No cost budget has been configured for this project.
          </p>
          <Button
            variant="link"
            className="mt-2"
            onClick={() =>
              navigate(`/projects/${selectedProjectId}?tab=cost-budget`)
            }
          >
            Set up Cost Budget →
          </Button>
        </div>
      )}

      {/* Report Content */}
      {selectedProjectId !== null && !loadingReport && hasCostBudget && reportData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <CompactKpiCard
              title="Total Cost Variance"
              value={<SensitiveData><VarianceValue value={costVariance} /></SensitiveData>}
              description={`${Math.abs(costVariancePct).toFixed(1)}% ${costVariance > 0 ? 'over' : 'under'} budget`}
              icon={costVariance > 0 ? TrendingUp : TrendingDown}
              accentVariant={budgetStatusToKpiAccent(costStatus)}
              className="shadow-none"
            />
            <CompactKpiCard
              title="Revenue Variance"
              value={<SensitiveData><VarianceValue value={revenueVariance} invertColour /></SensitiveData>}
              description={`${Math.abs(revenuePct).toFixed(1)}% ${revenueVariance < 0 ? 'behind' : 'ahead of'} plan`}
              icon={revenueVariance >= 0 ? TrendingUp : TrendingDown}
              accentVariant={budgetStatusToKpiAccent(revenueStatus)}
              className="shadow-none"
            />
            <CompactKpiCard
              title="Hours Variance"
              value={<span>{hoursVariance > 0 ? '+' : ''}{hoursVariance} hrs</span>}
              description={`${Math.abs(hoursPct).toFixed(1)}% ${hoursVariance > 0 ? 'over' : 'under'} budget`}
              icon={hoursVariance > 0 ? TrendingUp : hoursVariance < 0 ? TrendingDown : Minus}
              accentVariant={budgetStatusToKpiAccent(hoursStatus)}
              className="shadow-none"
            />
            <CompactKpiCard
              title="Available Budget"
              value={<SensitiveData>{formatCurrency(availableBudget)}</SensitiveData>}
              description={`of ${formatCurrency(totalBudget)} total budget`}
              icon={BarChart3}
              accentVariant={budgetStatusToKpiAccent(availableStatus)}
              className="shadow-none"
            />
          </div>

          {/* ── Revenue Variance Section ── */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Revenue
              </h2>
            </div>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">Category</TableHead>
                    <TableHead className="text-right">Budget</TableHead>
                    <TableHead className="text-right">Actual</TableHead>
                    <TableHead className="text-right">Variance £</TableHead>
                    <TableHead className="text-right">Variance %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Contract Revenue</TableCell>
                    <TableCell className="text-right tabular-nums">
                      <SensitiveData>{formatCurrency(reportData.revenue.budget)}</SensitiveData>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <SensitiveData>{formatCurrency(reportData.revenue.actual)}</SensitiveData>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <SensitiveData>
                        <VarianceValue value={revenueVariance} invertColour />
                      </SensitiveData>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <VarianceValue value={revenuePct} isPercent invertColour />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>

          {/* ── Cost Variance Section ── */}
          <div className="space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Costs
              </h2>
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showCommitted}
                  onChange={toggleCommitted}
                  className="rounded"
                />
                Show Committed
              </label>
            </div>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[180px]">Category</TableHead>
                    <TableHead className="text-right">Budget</TableHead>
                    <TableHead className="text-right">Actual</TableHead>
                    {showCommitted && (
                      <TableHead className="text-right">Committed</TableHead>
                    )}
                    <TableHead className="text-right">Available</TableHead>
                    <TableHead className="text-right">Variance £</TableHead>
                    <TableHead className="text-right">Variance %</TableHead>
                    <TableHead>Budget Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Total row */}
                  <TableRow className="font-semibold bg-muted/30">
                    <TableCell>
                      <div className="flex items-center gap-1.5 pl-5">
                        Total Cost
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <SensitiveData>{formatCurrency(totalBudget)}</SensitiveData>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <SensitiveData>{formatCurrency(costTotals?.actual ?? 0)}</SensitiveData>
                    </TableCell>
                    {showCommitted && (
                      <TableCell className="text-right tabular-nums">
                        <SensitiveData>{formatCurrency(costTotals?.committed ?? 0)}</SensitiveData>
                      </TableCell>
                    )}
                    <TableCell className="text-right tabular-nums">
                      <SensitiveData>
                        <span className={availableBudget < 0 ? 'text-destructive' : ''}>
                          {formatCurrency(availableBudget)}
                        </span>
                      </SensitiveData>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <SensitiveData>
                        <VarianceValue value={costVariance} />
                      </SensitiveData>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <VarianceValue value={costVariancePct} isPercent />
                    </TableCell>
                    <TableCell>
                      <HealthBadge status={costStatus} />
                    </TableCell>
                  </TableRow>

                  {/* Category rows — mirrors Cost Budget table:
                      Labour categories are rolled up into one expandable group row;
                      all other categories are flat (never expandable). */}
                  {(() => {
                    // Category type casing is not guaranteed by the API/recalc paths, so
                    // normalise before matching or Labour actuals fall out of the roll-up.
                    const isLabour = (type: string) => type.trim().toLowerCase() === 'labour';
                    const labourCats = costCategories.filter(c => isLabour(c.type));
                    const nonLabourCats = costCategories.filter(c => !isLabour(c.type));

                    // Rolled-up Labour totals
                    const labourBudget = labourCats.reduce((s, c) => s + c.budget, 0);
                    const labourActual = labourCats.reduce((s, c) => s + c.actual, 0);

                    // Synthetic Labour group category object
                    const labourGroupCat: CostCategory | BudgetVarianceCategory = labourCats.length === 1
                      ? labourCats[0]
                      : {
                          id: -1,
                          name: 'Labour',
                          type: 'labour',
                          scope: 'global',
                          budget: labourBudget,
                          actual: labourActual,
                          committed: 0,
                          available: labourBudget - labourActual,
                          progressPercent: labourBudget > 0 ? (labourActual / labourBudget) * 100 : 0,
                        };

                    return (
                      <>
                        {/* Labour group row (expandable) */}
                        {labourCats.length > 0 && (
                          <React.Fragment key="labour-group">
                            <CategoryRow
                              category={labourGroupCat}
                              budgetOverride={labourBudget}
                              actualOverride={labourActual}
                              showCommitted={showCommitted}
                              isExpanded={expandedCategories.has(-1)}
                              onToggle={() => toggleCategory(-1)}
                              isLabourGroup={true}
                            />
                            {expandedCategories.has(-1) && labourCats.map((cat) => (
                              <LabourSubRow
                                key={cat.id}
                                cat={cat}
                                showCommitted={showCommitted}
                              />
                            ))}
                          </React.Fragment>
                        )}

                        {/* Non-labour categories — flat, never expandable */}
                        {nonLabourCats.map((cat) => (
                          <CategoryRow
                            key={cat.id}
                            category={cat}
                            showCommitted={showCommitted}
                            isExpanded={false}
                            onToggle={() => {}}
                            isLabourGroup={false}
                          />
                        ))}
                      </>
                    );
                  })()}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* ── Hours Variance Section ── */}
          <div className="space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Hours
            </h2>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">Category</TableHead>
                    <TableHead className="text-right">Budget</TableHead>
                    <TableHead className="text-right">Actual</TableHead>
                    <TableHead className="text-right">Variance</TableHead>
                    <TableHead className="text-right">Variance %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Total Hours</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {reportData.hours.budget} hrs
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {reportData.hours.actual} hrs
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <span
                        className={
                          hoursVariance > 0
                            ? 'text-destructive'
                            : hoursVariance < 0
                              ? 'text-success-600 dark:text-success-500'
                              : 'text-muted-foreground'
                        }
                      >
                        {hoursVariance > 0 ? '+' : ''}
                        {hoursVariance} hrs
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <VarianceValue value={hoursPct} isPercent />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
